// @givmo/app-charity — request-to-be-removed flow (CPA-WEB7).
//
// The charity-facing "remove this organization from Givmo" flow — the page at `/removal`, reached from the
// composed Settings page's Organization card (Org Machinery OM-17). It is a CONFIRM-FIRST
// flow: the Owner writes a brief reason, then a confirm dialog spells out — BEFORE anything fires — the
// plain-language consequences, and only the explicit confirm inside the dialog calls the mutation
// (`charityAdminRequestCharityRemoval`, POST …/removal-request, Owner-only, `written_request_reason`
// required). The 201 result is the new (open) request; the parent invalidates the list so the pending panel
// takes over.
//
// ★ THE HONEST-CONSEQUENCES RAIL (plan D-CP-11 — "unclaimed ≠ unpaid"; D-CP-94 — what a processed removal
// does): portal access ends for everyone on the team and pending invitations are cancelled, the
// organization is removed from Givmo for donors, and its mission, contact details, logo, profile page and the
// organization details it entered are removed (its website, its bank details and the IRS record stay); Givmo
// staff process it within a 3+3-business-day SLA after an identity check. It DOES NOT stop the charity from
// getting its money: owed grants keep reaching it, to its bank account if that account is approved for
// payments, otherwise by mailed check. The copy states that plainly and NEVER implies removal stops or
// changes grant payments — a charity must never fear that leaving means losing money it is owed.
//
// Owner-only: the parent renders this only for a confirmed Owner (fail closed); the backend also 403s a
// non-Owner. `data-testid`s on the reason field, the request button, the confirm dialog, its consequence
// copy, and the payout-continues line back the e2e/unit assertions.

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { charityAdminRequestCharityRemovalMutation } from '@givmo/api-client/query';
import { isGivmoError, type CharityRemovalRequestOut } from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  Icon,
  Textarea,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { AlertTriangle, BadgeCheck, LogOut } from 'lucide-react';
import {
  EMPTY_REMOVAL_FORM,
  REASON_MAX,
  REASON_MESSAGE,
  removalRequestSchema,
  type RemovalRequestFormValues,
} from './removal-schemas';
import { useUnsavedChanges } from '../../lib/unsaved-changes';
import { REMOVAL_SLA_COPY } from './removal-decode';

const PREFIX = 'settings-removal';

export interface RemovalRequestFormProps {
  charityId: string;
  /** The organization name — shown in the confirm dialog so the Owner sees exactly what they're removing. */
  orgName: string;
  /** Called with the new (open) request after a successful submit; the parent invalidates the list. */
  onRequested: (request: CharityRemovalRequestOut) => void | Promise<unknown>;
}

export function RemovalRequestForm({ charityId, orgName, onRequested }: RemovalRequestFormProps) {
  const [form, setForm] = useState<RemovalRequestFormValues>(EMPTY_REMOVAL_FORM);
  const [touched, setTouched] = useState(false);
  // The confirm-first gate: the button opens this; only the confirm INSIDE it fires the mutation.
  const [confirmOpen, setConfirmOpen] = useState(false);

  const request = useMutation({
    ...charityAdminRequestCharityRemovalMutation(),
    onSuccess: async (result) => {
      setConfirmOpen(false);
      setForm(EMPTY_REMOVAL_FORM);
      setTouched(false);
      toast.success('Removal requested. We’ll verify your identity and follow up.');
      await onRequested(result);
    },
    onError: (error) => {
      setConfirmOpen(false);
      toast.error(
        isGivmoError(error)
          ? error.message
          : 'We couldn’t submit your removal request. Please try again.',
      );
    },
  });

  const parsed = removalRequestSchema.safeParse(form);
  const isValid = parsed.success;
  const isDirty = form.written_request_reason.trim().length > 0;
  useUnsavedChanges(isDirty && !request.isPending);
  const reasonError = touched && !isValid ? REASON_MESSAGE : undefined;

  function submitRequest() {
    if (!parsed.success || request.isPending) return;
    request.mutate({
      path: { charity_id: charityId },
      body: { written_request_reason: parsed.data.written_request_reason },
    });
  }

  return (
    <Card testId={`${PREFIX}-card`}>
      <CardHeader>
        <div className="flex items-center gap-10">
          <span className="flex size-32 items-center justify-center rounded-full bg-danger-50 text-danger-600">
            <Icon icon={LogOut} size={18} aria-hidden />
          </span>
          <div>
            <div className="font-w600 text-text-strong">Remove this organization from Givmo</div>
            <div className="text-s13 text-text-tertiary">
              Removes this organization from Givmo for donors and ends your team’s access.
            </div>
          </div>
        </div>
      </CardHeader>
      <CardBody className="flex flex-col gap-16">
        {/* The honest up-front summary — payouts continue. This is the same rail the dialog restates. */}
        <div
          className="flex items-start gap-10 rounded-lg border border-border-subtle bg-surface-sunken p-14"
          {...testIdProp(`${PREFIX}-summary`)}
        >
          <Icon icon={BadgeCheck} size={18} className="mt-1 shrink-0 text-brand-600" aria-hidden />
          <p className="text-s13 text-text-secondary">
            Removing your organization ends your team’s access to this portal and removes it from
            Givmo for donors. It does <span className="font-w600">not</span> affect any grant
            payments: your organization keeps receiving the money it’s owed, to your bank account if
            it’s approved for payments, otherwise by mailed check. Claiming was only ever about
            speed and insight, never a condition for receiving grant money.
          </p>
        </div>

        <Textarea
          label="Why are you requesting removal?"
          placeholder="Tell us briefly why you’d like to leave (required)."
          helper="This is recorded with your request. Our team reviews every written request."
          rows={3}
          maxLength={REASON_MAX}
          value={form.written_request_reason}
          onChange={(e) => setForm({ written_request_reason: e.target.value })}
          onBlur={() => setTouched(true)}
          error={!!reasonError}
          errorMessage={reasonError}
          testId={`${PREFIX}-reason`}
        />

        <div className="flex items-center justify-end">
          <Button
            appearance="saas"
            tone="danger"
            leadingIcon={<Icon icon={LogOut} size={16} aria-hidden />}
            disabled={!isValid}
            onClick={() => {
              setTouched(true);
              if (isValid) setConfirmOpen(true);
            }}
            testId={`${PREFIX}-request-button`}
          >
            Request removal
          </Button>
        </div>
      </CardBody>

      {/* Confirm-first: the consequences are spelled out BEFORE anything fires. Only the confirm INSIDE
          this dialog calls the mutation. */}
      <Dialog.Root
        open={confirmOpen}
        onOpenChange={(open) => !request.isPending && setConfirmOpen(open)}
      >
        <Dialog.Content aria-label="Confirm removal request" testId={`${PREFIX}-confirm-dialog`}>
          <div className="flex flex-col gap-16">
            <div className="flex items-start gap-12">
              <span className="flex size-[40px] shrink-0 items-center justify-center rounded-full bg-danger-50 text-danger-600">
                <Icon icon={AlertTriangle} size={20} aria-hidden />
              </span>
              <div className="flex flex-col gap-4">
                <Dialog.Title className="font-w700 text-h6 text-text-strong">
                  Remove {orgName} from Givmo?
                </Dialog.Title>
                <Dialog.Description className="text-s14 text-text-secondary">
                  Here’s exactly what happens if you continue.
                </Dialog.Description>
              </div>
            </div>

            {/* Plain-language consequences. */}
            <ul
              className="flex flex-col gap-10 text-s13 text-text-secondary"
              {...testIdProp(`${PREFIX}-consequences`)}
            >
              <li className="flex items-start gap-8">
                <span
                  aria-hidden
                  className="mt-[6px] size-[6px] shrink-0 rounded-full bg-text-tertiary"
                />
                <span>
                  Portal access ends for everyone on your team, and pending invitations are
                  cancelled.
                </span>
              </li>
              <li className="flex items-start gap-8">
                <span
                  aria-hidden
                  className="mt-[6px] size-[6px] shrink-0 rounded-full bg-text-tertiary"
                />
                <span>
                  Your organization is removed from Givmo for donors, so they can no longer find it
                  here.
                </span>
              </li>
              <li className="flex items-start gap-8">
                <span
                  aria-hidden
                  className="mt-[6px] size-[6px] shrink-0 rounded-full bg-text-tertiary"
                />
                <span>
                  Your mission, contact details, logo, profile page and the organization details you
                  entered are removed.
                </span>
              </li>
              <li className="flex items-start gap-8">
                <span
                  aria-hidden
                  className="mt-[6px] size-[6px] shrink-0 rounded-full bg-text-tertiary"
                />
                <span>
                  Our team verifies your identity and then processes the removal, typically within{' '}
                  {REMOVAL_SLA_COPY}.
                </span>
              </li>
              {/* ★ The payout-continues line — the D-CP-11 rail, stated plainly. */}
              <li className="flex items-start gap-8" {...testIdProp(`${PREFIX}-payout-note`)}>
                <span
                  aria-hidden
                  className="mt-[6px] size-[6px] shrink-0 rounded-full bg-success-500"
                />
                <span>
                  <span className="font-w600 text-text-strong">
                    Your grant payments are not affected.
                  </span>{' '}
                  Any grants your organization is owed still reach it, to your bank account if it’s
                  approved for payments, otherwise by mailed check. Removal never stops or reduces
                  the money your organization is owed.
                </span>
              </li>
            </ul>

            <div className="flex items-center justify-end gap-8">
              <Dialog.Close
                render={
                  <Button
                    appearance="saas"
                    tone="secondary"
                    size="sm"
                    disabled={request.isPending}
                    testId={`${PREFIX}-confirm-cancel`}
                  >
                    Keep my access
                  </Button>
                }
              />
              <Button
                appearance="saas"
                tone="danger"
                size="sm"
                loading={request.isPending}
                onClick={submitRequest}
                testId={`${PREFIX}-confirm-submit`}
              >
                Request removal
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Root>
    </Card>
  );
}
